import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { CalendarClock, CalendarDays, Video, Phone, MessageCircle, ShieldCheck, CreditCard, ArrowRight, Loader2, PartyPopper, FileSearch, UserCheck, Lightbulb, HelpCircle, FolderCheck } from "lucide-react";
import { format, parseISO, startOfToday } from "date-fns";
import { fr } from "date-fns/locale";
import api, { apiErrorMessage } from "@/lib/api";
import { trackAppointment } from "@/lib/analytics";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Section, Overline, Reveal } from "@/components/Primitives";
import { AdvisorsSection } from "@/components/AdvisorsSection";
import { Seo } from "@/components/Seo";

// Paysages premium — le fond du hero change selon la destination choisie
const HERO_IMAGES = {
  canada: "https://images.unsplash.com/photo-1572204292164-b35ba943fca7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  australie: "https://images.unsplash.com/photo-1590716209211-ea74d5f63573?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
};

const BENEFITS = [
  { icon: FileSearch, text: "Une étude personnalisée de votre projet" },
  { icon: UserCheck, text: "Une vérification de votre éligibilité" },
  { icon: Lightbulb, text: "Des conseils adaptés à votre situation" },
  { icon: HelpCircle, text: "Des réponses à toutes vos questions" },
  { icon: FolderCheck, text: "Une préparation complète de votre dossier" },
];

const MODES = [
  { id: "visio", label: "Visioconférence", icon: Video },
  { id: "telephone", label: "Téléphone", icon: Phone },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
];

const DESTINATIONS = [
  { id: "australie", label: "Australie", flag: "🇦🇺" },
  { id: "canada", label: "Canada", flag: "🇨🇦" },
];

export default function Booking() {
  const params = new URLSearchParams(window.location.search);
  const [form, setForm] = useState({
    destination: params.get("destination") || "australie",
    mode: "visio", date: "", slot: "", name: "", email: "", phone: "", note: "",
  });
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;
  const disabledDate = (d) => d < startOfToday() || isWeekend(d);

  useEffect(() => {
    if (!form.date) { setSlots([]); return; }
    setLoadingSlots(true);
    setForm((f) => ({ ...f, slot: "" }));
    api.get(`/appointments/availability?date=${form.date}`)
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [form.date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.slot) { toast.error("Veuillez choisir une date et un créneau."); return; }
    if (!form.name || !form.email) { toast.error("Veuillez renseigner votre nom et votre email."); return; }
    setSubmitting(true);
    try {
      const preferred_date = `${form.date}T${form.slot}`;
      const { data } = await api.post("/appointments/public", {
        name: form.name, email: form.email, phone: form.phone,
        destination: form.destination, mode: form.mode, preferred_date, note: form.note,
      });
      setDone({ ...form, preferred_date });
      trackAppointment(form.destination);
      toast.success(data.message || "Rendez-vous enregistré !");
    } catch (err) {
      toast.error(apiErrorMessage(err?.response?.data?.detail));
      if (err?.response?.status === 409 && form.date) {
        // rafraîchir les créneaux
        const r = await api.get(`/appointments/availability?date=${form.date}`).catch(() => null);
        if (r) setSlots(r.data.slots || []);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const modeLabel = MODES.find((m) => m.id === form.mode)?.label;
  const destLabel = DESTINATIONS.find((d) => d.id === form.destination);

  return (
    <div data-testid="booking-page">
      <Seo
        title="Prendre rendez-vous gratuitement — Consultation visa Australie & Canada | Work & Travel Expert"
        description="Réservez votre consultation gratuite et sans engagement avec un conseiller Work & Travel Expert. Étude personnalisée de votre projet Australie ou Canada."
        keywords="rendez-vous gratuit, consultation visa, prendre rendez-vous immigration australie canada"
        path="/rendez-vous"
      />

      {/* HERO — fond immersif qui change selon la destination choisie (Canada 🇨🇦 / Australie 🇦🇺) */}
      <div className="relative bg-navy overflow-hidden" data-testid="booking-hero">
        {/* Deux paysages superposés, fondu croisé selon form.destination */}
        <img
          src={HERO_IMAGES.canada}
          alt="Paysages du Canada — lac turquoise et montagnes"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[900ms] ease-out ${form.destination === "canada" ? "opacity-100" : "opacity-0"}`}
        />
        <img
          src={HERO_IMAGES.australie}
          alt="Paysages d'Australie — Opéra de Sydney au coucher du soleil"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[900ms] ease-out ${form.destination === "australie" ? "opacity-100" : "opacity-0"}`}
        />
        <div className="absolute inset-0 bg-navy/75" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/55 to-navy/40" />
        <div className="pointer-events-none absolute -top-40 -right-24 w-[34rem] h-[34rem] rounded-full bg-gold/15 blur-[120px]" />
        <Section className="relative z-10 py-16 sm:py-24">
          <Reveal>
            <Overline>✈️ Votre projet commence ici</Overline>
            <h1 className="mt-4 font-heading font-medium text-white leading-[1.05] tracking-tight text-4xl sm:text-5xl lg:text-6xl">
              Prenez rendez-vous <span className="text-gold">gratuitement</span>
            </h1>
            <p className="mt-4 text-gold text-lg sm:text-xl">Consultation offerte · Sans engagement</p>
            <p className="mt-5 max-w-2xl text-white/85 text-base sm:text-lg leading-relaxed">
              Vous souhaitez partir travailler au Canada 🇨🇦 ou en Australie 🇦🇺 ? Profitez d'une consultation gratuite avec un conseiller pour préparer votre projet en toute sérénité.
            </p>
            {/* Sélecteur de destination — pilote aussi le fond du hero */}
            <div className="mt-7 inline-flex rounded-full border border-white/25 bg-white/10 p-1 backdrop-blur-md" data-testid="booking-hero-dest">
              {DESTINATIONS.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  data-testid={`booking-hero-dest-${d.id}`}
                  onClick={() => set("destination", d.id)}
                  className={`inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${form.destination === d.id ? "bg-gold text-navy shadow-lg" : "text-white/80 hover:text-white"}`}
                >
                  <span className="text-base">{d.flag}</span> {d.label}
                </button>
              ))}
            </div>
          </Reveal>
        </Section>
      </div>

      {/* NOS CONSEILLERS — rassure avant la réservation (gérable depuis l'admin) */}
      <AdvisorsSection />

      {/* CONTENU + FORMULAIRE */}
      <Section className="py-16">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Colonne info */}
          <Reveal>
            <h2 className="font-heading text-2xl sm:text-3xl text-navy">Lors de votre rendez-vous, vous bénéficierez de :</h2>
            <div className="gold-line mt-5" />
            <div className="mt-7 grid sm:grid-cols-2 gap-4" data-testid="booking-benefits">
              {BENEFITS.map((b, i) => (
                <div
                  key={i}
                  data-testid={`booking-benefit-${i}`}
                  className="group flex items-start gap-4 rounded-xl border border-border bg-white p-4 shadow-sm transition-all hover:shadow-md hover:border-gold/50 hover:-translate-y-0.5"
                >
                  <span className="shrink-0 flex h-11 w-11 items-center justify-center rounded-lg bg-gold/10 text-gold transition-colors group-hover:bg-gold group-hover:text-navy">
                    <b.icon size={22} />
                  </span>
                  <span className="text-slate-700 leading-snug pt-0.5">{b.text}</span>
                </div>
              ))}
            </div>

            <h3 className="mt-10 font-heading text-xl text-navy">Pourquoi prendre rendez-vous dès aujourd'hui ?</h3>
            <p className="mt-3 text-slate-700 leading-relaxed">
              Nos conseillers sont disponibles dès maintenant. Faites le point sur votre projet, vérifiez votre éligibilité et démarrez votre accompagnement sans attendre.
            </p>

            <div className="mt-8 rounded-xl border border-gold/40 bg-[#F8F9FA] p-6">
              <p className="flex items-center gap-2 font-heading text-lg text-navy"><CreditCard size={20} className="text-gold" /> Consultation 100 % gratuite</p>
              <p className="mt-3 text-sm text-slate-700 leading-relaxed">
                La consultation est entièrement gratuite et sans engagement. Si vous le souhaitez, vous pouvez aussi choisir et régler directement l'un de nos packs en ligne, en toute sécurité.
              </p>
            </div>
          </Reveal>

          {/* Colonne formulaire / confirmation */}
          <Reveal delay={0.1}>
            {done ? (
              <div data-testid="booking-success" className="rounded-2xl border border-gold/40 bg-navy text-white p-8 sm:p-10 text-center">
                <div className="mx-auto h-16 w-16 rounded-full bg-gold/15 flex items-center justify-center text-gold"><PartyPopper size={34} /></div>
                <h2 className="mt-5 font-heading text-2xl sm:text-3xl">Rendez-vous enregistré 🎉</h2>
                <p className="mt-4 text-white/80 leading-relaxed">
                  Merci {done.name} ! Votre demande de consultation gratuite (<strong>{modeLabel}</strong>) pour le <strong>{done.preferred_date.replace("T", " à ")}</strong> a bien été prise en compte.
                </p>
                <p className="mt-3 text-white/70 text-sm">Vous recevrez un e-mail de confirmation à <strong>{done.email}</strong> dès validation par notre équipe.</p>
                <button data-testid="booking-new" onClick={() => { setDone(null); setForm((f) => ({ ...f, date: "", slot: "" })); }} className="mt-7 inline-flex items-center gap-2 border border-white/40 text-white px-6 py-3 rounded-full hover:bg-white hover:text-navy transition-colors text-sm font-medium">
                  Prendre un autre rendez-vous
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="booking-form" className="rounded-2xl border border-border bg-white shadow-xl p-6 sm:p-8">
                <p className="inline-flex items-center gap-2 rounded-full bg-gold/10 border border-gold/40 text-navy text-xs font-semibold uppercase tracking-wide px-4 py-1.5">
                  <CalendarClock size={15} className="text-gold" /> Réservez votre créneau
                </p>

                {/* Destination */}
                <Label className="block mt-6 mb-2 text-navy font-medium">Destination</Label>
                <div className="grid grid-cols-2 gap-3">
                  {DESTINATIONS.map((d) => (
                    <button type="button" key={d.id} data-testid={`booking-dest-${d.id}`} onClick={() => set("destination", d.id)}
                      className={`flex items-center justify-center gap-2 py-3 rounded-lg border-2 text-sm font-medium transition-colors ${form.destination === d.id ? "border-gold bg-gold/10 text-navy" : "border-border text-slate-600 hover:border-gold/50"}`}>
                      <span className="text-lg">{d.flag}</span> {d.label}
                    </button>
                  ))}
                </div>

                {/* Mode */}
                <Label className="block mt-5 mb-2 text-navy font-medium">Type de rendez-vous</Label>
                <div className="grid grid-cols-3 gap-2.5">
                  {MODES.map((m) => (
                    <button type="button" key={m.id} data-testid={`booking-mode-${m.id}`} onClick={() => set("mode", m.id)}
                      className={`flex flex-col items-center gap-1.5 py-3 rounded-lg border-2 text-xs font-medium transition-colors ${form.mode === m.id ? "border-gold bg-gold/10 text-navy" : "border-border text-slate-600 hover:border-gold/50"}`}>
                      <m.icon size={20} className={form.mode === m.id ? "text-gold" : ""} /> {m.label}
                    </button>
                  ))}
                </div>

                {/* Date */}
                <Label className="block mt-5 mb-2 text-navy font-medium">Date souhaitée <span className="text-muted-foreground font-normal">(Lun–Ven)</span></Label>
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      id="booking-date"
                      data-testid="booking-date"
                      className={`flex h-11 w-full items-center gap-2.5 rounded-md border border-border bg-white px-3 text-left text-sm transition-colors hover:border-gold/60 focus:outline-none focus:ring-2 focus:ring-gold/40 ${form.date ? "text-navy" : "text-muted-foreground"}`}
                    >
                      <CalendarDays size={18} className="text-gold shrink-0" />
                      {form.date
                        ? format(parseISO(form.date), "EEEE d MMMM yyyy", { locale: fr })
                        : "Choisissez une date…"}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start" data-testid="booking-date-popover">
                    <Calendar
                      mode="single"
                      locale={fr}
                      weekStartsOn={1}
                      selected={form.date ? parseISO(form.date) : undefined}
                      disabled={disabledDate}
                      defaultMonth={form.date ? parseISO(form.date) : new Date()}
                      onSelect={(d) => { if (d) { set("date", format(d, "yyyy-MM-dd")); setDateOpen(false); } }}
                    />
                  </PopoverContent>
                </Popover>

                {/* Slots */}
                {form.date && (
                  <div className="mt-4">
                    <Label className="block mb-2 text-navy font-medium">Créneaux disponibles</Label>
                    {loadingSlots ? (
                      <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Chargement…</p>
                    ) : slots.length === 0 ? (
                      <p data-testid="booking-no-slots" className="text-sm text-muted-foreground">Aucun créneau disponible ce jour. Choisissez une autre date (du lundi au vendredi).</p>
                    ) : (
                      <div className="grid grid-cols-4 sm:grid-cols-5 gap-2" data-testid="booking-slots">
                        {slots.map((s) => (
                          <button type="button" key={s} data-testid={`booking-slot-${s}`} onClick={() => set("slot", s)}
                            className={`py-2 rounded-md border text-sm transition-colors ${form.slot === s ? "border-gold bg-gold text-white" : "border-border text-navy hover:border-gold"}`}>
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Coordonnées */}
                <div className="grid sm:grid-cols-2 gap-3 mt-5">
                  <div>
                    <Label htmlFor="booking-name" className="mb-1.5 block text-navy font-medium">Nom complet</Label>
                    <Input id="booking-name" data-testid="booking-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Votre nom" required />
                  </div>
                  <div>
                    <Label htmlFor="booking-phone" className="mb-1.5 block text-navy font-medium">Téléphone</Label>
                    <Input id="booking-phone" data-testid="booking-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+33 …" />
                  </div>
                </div>
                <div className="mt-3">
                  <Label htmlFor="booking-email" className="mb-1.5 block text-navy font-medium">Email</Label>
                  <Input id="booking-email" data-testid="booking-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="vous@email.com" required />
                </div>
                <div className="mt-3">
                  <Label htmlFor="booking-note" className="mb-1.5 block text-navy font-medium">Message (optionnel)</Label>
                  <Textarea id="booking-note" data-testid="booking-note" value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="Parlez-nous de votre projet…" rows={3} />
                </div>

                <button type="submit" data-testid="booking-submit" disabled={submitting}
                  className="group mt-6 w-full inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover text-navy font-bold uppercase tracking-wide py-4 rounded-full transition-all disabled:opacity-60">
                  {submitting ? <><Loader2 size={18} className="animate-spin" /> Envoi…</> : <>Prendre rendez-vous gratuitement <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" /></>}
                </button>
                <p className="mt-3 text-center text-xs text-muted-foreground flex items-center justify-center gap-1.5">
                  <ShieldCheck size={14} className="text-gold" /> Sans engagement · Aucun paiement requis aujourd'hui
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </Section>
    </div>
  );
}
